import type { ScopeData } from '../types'
import { COUNTRIES } from '@features/dashboards/components/PocTool/data/countries'
import { getIdTypesForCountry } from '@features/dashboards/components/MatchRateTool/data/idTypes'
import { MATURITY_DIMENSIONS } from './defaults'

const MEDDPICC_KEYS = [
  { key: 'vision___success_criteria', label: 'Metrics' },
  { key: 'economic_buyer', label: 'Economic Buyer' },
  { key: 'decision_criteria', label: 'Decision Criteria' },
  { key: 'decision_process', label: 'Decision Process' },
  { key: 'paper_process', label: 'Paper Process' },
  { key: 'identify_pain', label: 'Identify Pain' },
  { key: 'champion', label: 'Champion' },
  { key: 'competitor', label: 'Competition' },
]

function countryName(code: string): string {
  return COUNTRIES.find((c) => c.code === code)?.name || code
}

function idLabel(code: string, country: string): string {
  const types = getIdTypesForCountry(country)
  return types?.find((t) => t.id === code)?.label || code
}

const FUZZ_LABELS: Record<string, string> = { strict: 'Strict', moderate: 'Moderate', fuzzy: 'Fuzzy', optional: 'Optional', unknown: 'Unknown' }
const CAPTURE_LABELS: Record<string, string> = {
  ocr: 'OCR from document', user_vendor_form: 'User input (vendor form)', user_client_form: 'User input (client form)',
  existing: 'Existing record', lookup: 'Lookup / enrichment', other: 'Other (see notes)',
}
const ADDR_LABELS: Record<string, string> = {
  single_line: 'Single line / free text', line1_line2: 'Address line 1 + line 2', structured: 'Structured',
  google_places: 'Google Places API', other_autocomplete: 'Other autocomplete',
}

export function generateBlueprintText(scope: ScopeData, qualification: Record<string, string> | null): string {
  const bp = scope.blueprintConfig
  const lines: string[] = []
  const hr = () => lines.push('─'.repeat(60))

  lines.push(`PRE-SALES BLUEPRINT — ${scope.customerName || '(no customer)'}`)
  hr()

  // MEDDPICC
  lines.push('\n## MEDDPICC (Sales-owned, from HubSpot)\n')
  if (qualification) {
    for (const m of MEDDPICC_KEYS) {
      const val = qualification[m.key]?.trim()
      lines.push(`  ${m.label}: ${val || '(empty)'}`)
    }
  } else {
    lines.push('  No HubSpot data linked.')
  }

  // Context & Metrics
  lines.push('\n## Context & Current Metrics\n')
  lines.push(`  Business Model: ${bp.businessModel || '(empty)'}`)
  lines.push(`  Monthly Onboarding Volume: ${bp.monthlyOnboardingVolume || '(empty)'}`)
  lines.push(`  Strategic Priorities: ${bp.strategicPriorities || '(empty)'}`)
  lines.push(`  STP Rate: ${bp.stpRate || '(empty)'}${bp.stpRateConfirmed ? ' (confirmed)' : ' (unconfirmed)'}`)
  lines.push(`  Fraud Rate: ${bp.fraudRate || '(empty)'}${bp.fraudRateConfirmed ? ' (confirmed)' : ' (unconfirmed)'}`)

  // Problem Hypothesis
  lines.push('\n## Customer Problem Hypothesis\n')
  lines.push(`  Problem Hypothesis: ${bp.problemHypothesis || '(empty)'}`)
  lines.push(`  Surface Pain: ${bp.surfacePain || '(empty)'}`)
  lines.push(`  Deep Pain: ${bp.deepPain || '(empty)'}`)
  lines.push(`  Why Now?: ${bp.whyNow || '(empty)'}`)
  if (bp.regulatoryContext) {
    lines.push(`  Regulatory & Industry Context: ${bp.regulatoryContext}`)
  }

  // As-Is Architecture
  lines.push('\n## As-Is Architecture\n')
  lines.push(`  Current Verification Process: ${bp.currentProcess || '(empty)'}`)
  lines.push(`  Integration Method: ${bp.integrationMethod || '(empty)'}`)
  lines.push('')
  lines.push('  Discovery Questions:')
  lines.push(`    Jurisdictions in scope: ${bp.jurisdictionsInScope || '(empty)'}`)
  lines.push(`    Current onboarding flow: ${bp.onboardingJourney || '(empty)'}`)
  lines.push(`    Checks before transact: ${bp.checksBeforeTransact || '(empty)'}`)
  lines.push(`    Risk segmentation: ${bp.riskSegmentation || '(empty)'}`)
  lines.push(`    EDD / manual review triggers: ${bp.eddTriggers || '(empty)'}`)
  lines.push(`    Sanctions, PEP & adverse media: ${bp.sanctionsPepScreening || '(empty)'}`)
  lines.push(`    Ongoing monitoring: ${bp.ongoingMonitoring || '(empty)'}`)
  lines.push(`    Friction & false positives: ${bp.frictionFalsePositives || '(empty)'}`)
  if (bp.currentVendors.length > 0) {
    lines.push('  Current Vendors:')
    for (const v of bp.currentVendors) {
      lines.push(`    - ${v.vendorName} (${v.function})${v.notes ? ` — ${v.notes}` : ''}`)
    }
  }

  // Verification Stack
  lines.push('\n## Verification Stack\n')
  if ((bp.verificationStack || []).length === 0) {
    lines.push('  No verification stack documented.')
  }
  for (const v of (bp.verificationStack || [])) {
    const label = v.layer === 'other' ? (v.customLabel || '(custom)') : v.layer
    lines.push(`  ${label}: ${v.vendor || '(no vendor)'} | Owner: ${v.owner || '—'} | Pain: ${v.painPoints || '—'}`)
  }

  // Country Rulesets
  lines.push('\n## Country Rulesets\n')
  if (bp.countryRulesets.length === 0) {
    lines.push('  No countries documented.')
  }
  for (const cr of bp.countryRulesets) {
    const name = cr.country ? countryName(cr.country) : '(no country)'
    lines.push(`  ### ${name} (${cr.country || '??'})`)
    lines.push(`    Ruleset: ${cr.ruleset}`)
    lines.push(`    Gov ID Required: ${cr.govIdRequired ? 'Yes' : 'No'}`)
    lines.push(`    Current Pass Rate: ${cr.passRate || '—'}% ${cr.passRateConfirmed ? '(confirmed)' : '(unconfirmed)'}`)
    lines.push(`    Target Pass Rate: ${cr.desiredPassRate || '—'}% ${cr.desiredPassRateConfirmed ? '(confirmed)' : '(unconfirmed)'}`)

    const enabled = (cr.dataInputs || []).filter((di) => di.enabled)
    if (enabled.length > 0) {
      lines.push('    Data Inputs:')
      for (const di of enabled) {
        const parts = [
          `${di.type}`,
          `fuzziness=${FUZZ_LABELS[di.fuzziness] || di.fuzziness}`,
          di.captureMethod ? `capture=${CAPTURE_LABELS[di.captureMethod] || di.captureMethod}` : null,
          di.type === 'Address' && di.addressFormat ? `format=${ADDR_LABELS[di.addressFormat] || di.addressFormat}` : null,
          di.confirmed ? 'CONFIRMED' : 'UNCONFIRMED',
          di.notes ? `notes: ${di.notes}` : null,
        ].filter(Boolean)
        lines.push(`      - ${parts.join(' | ')}`)
      }
    }

    if ((cr.nationalIds || []).length > 0) {
      lines.push(`    National IDs: ${cr.nationalIds.map((id) => idLabel(id, cr.country)).join(', ')}`)
    }
    const vendors: string[] = []
    if ((cr.docScanVendors || []).length) vendors.push(`Doc Scan: ${cr.docScanVendors.join(', ')}`)
    if ((cr.selfieVendors || []).length) vendors.push(`Selfie: ${cr.selfieVendors.join(', ')}`)
    if ((cr.deviceDataVendors || []).length) vendors.push(`Device: ${cr.deviceDataVendors.join(', ')}`)
    if (vendors.length) lines.push(`    Vendors: ${vendors.join(' | ')}`)
    if (cr.notes) lines.push(`    Notes: ${cr.notes}`)
    lines.push('')
  }

  // Stakeholders
  lines.push('## Technical Stakeholders\n')
  if (bp.stakeholders.length === 0) {
    lines.push('  No stakeholders mapped.')
  }
  for (const s of bp.stakeholders) {
    lines.push(`  - ${s.name || '(unnamed)'} — ${s.role || '(no role)'} [${s.engagementState}]`)
    if (s.technicalConcerns) lines.push(`    Concerns: ${s.technicalConcerns}`)
  }

  // Maturity Assessment
  lines.push('\n## Maturity Assessment\n')
  for (const d of (bp.maturityDimensions || [])) {
    const dimConfig = MATURITY_DIMENSIONS.find((md) => md.id === d.id)
    const stageLabel = d.stage > 0 && dimConfig ? `Stage ${d.stage}: ${dimConfig.stages[d.stage - 1]}` : 'Not assessed'
    lines.push(`  ${d.dimension}: ${stageLabel}${d.evidence ? ` — ${d.evidence}` : ''}`)
  }

  // Priority Gaps
  if ((bp.priorityGaps || []).length > 0) {
    lines.push('\n## Priority Gaps\n')
    for (const g of (bp.priorityGaps || [])) {
      lines.push(`  ${g.rank}. ${g.description || '(empty)'}${g.impact ? ` — Impact: ${g.impact}` : ''}`)
    }
  }

  // Recommended Path
  const hasPath = (bp.quickWins || []).length > 0 || (bp.foundationMoves || []).length > 0 || (bp.strategicShifts || []).length > 0
  if (hasPath) {
    lines.push('\n## Recommended Path\n')
    if ((bp.quickWins || []).length > 0) {
      lines.push('  Quick Wins (0–3 months):')
      for (const a of bp.quickWins) lines.push(`    - ${a.text || '(empty)'}`)
    }
    if ((bp.foundationMoves || []).length > 0) {
      lines.push('  Foundation Moves (3–9 months):')
      for (const a of bp.foundationMoves) lines.push(`    - ${a.text || '(empty)'}`)
    }
    if ((bp.strategicShifts || []).length > 0) {
      lines.push('  Strategic Shifts (9–18 months):')
      for (const a of bp.strategicShifts) lines.push(`    - ${a.text || '(empty)'}`)
    }
  }

  // Post-call updates
  if (bp.postCallUpdates.length > 0) {
    lines.push('\n## Post-Call Updates\n')
    for (const u of [...bp.postCallUpdates].sort((a, b) => b.date.localeCompare(a.date))) {
      lines.push(`  ${u.date} — ${u.callWith || '(no attendees)'}`)
      if (u.hypothesisChanges) lines.push(`    Hypothesis changes: ${u.hypothesisChanges}`)
      if (u.gapsRemaining) lines.push(`    Gaps: ${u.gapsRemaining}`)
      if (u.nextCallProbes) lines.push(`    Next probes: ${u.nextCallProbes}`)
      lines.push('')
    }
  }

  // Prep checklist
  lines.push('## Preparation Checklist\n')
  for (const c of bp.prepChecklist) {
    lines.push(`  [${c.checked ? 'x' : ' '}] ${c.label}`)
  }

  return lines.join('\n')
}

// --- Helpers for the customer-facing HTML export ---

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')
}

const RULESET_DISPLAY: Record<string, string> = { '1+1': '1+1 (One document + one data source)', '2+2': '2+2 (Two documents + two data sources)', custom: 'Custom', unknown: 'Awaiting Confirmation' }

function buildCountryCard(cr: ScopeData['blueprintConfig']['countryRulesets'][number]): string {
  const name = cr.country ? countryName(cr.country) : 'Unknown'
  const ruleset = RULESET_DISPLAY[cr.ruleset] || cr.ruleset
  const rulesetColor = cr.ruleset === '1+1' ? '#3b82f6' : cr.ruleset === '2+2' ? '#8b5cf6' : cr.ruleset === 'custom' ? '#f59e0b' : '#ef4444'

  const enabled = (cr.dataInputs || []).filter((di) => di.enabled)

  let dataInputsHtml = ''
  if (enabled.length > 0) {
    const rows = enabled.map((di) => {
      const fuzz = FUZZ_LABELS[di.fuzziness] || di.fuzziness
      const capture = di.captureMethod ? (CAPTURE_LABELS[di.captureMethod] || di.captureMethod) : '—'
      const statusBadge = di.confirmed
        ? '<span style="display:inline-block;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600;background:#d1fae5;color:#065f46;">Confirmed</span>'
        : '<span style="display:inline-block;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600;background:#fef3c7;color:#92400e;">To Confirm</span>'
      return `<tr>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-weight:500;">${esc(di.type)}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;text-align:center;"><span style="display:inline-block;padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600;background:${fuzz === 'Strict' ? '#dbeafe' : fuzz === 'Fuzzy' ? '#fef3c7' : '#f3e8ff'};color:${fuzz === 'Strict' ? '#1d4ed8' : fuzz === 'Fuzzy' ? '#b45309' : '#7c3aed'};">${fuzz}</span></td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#6b7280;">${esc(capture)}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;text-align:center;">${statusBadge}</td>
      </tr>`
    }).join('')

    dataInputsHtml = `
      <div style="margin-top:16px;">
        <h4 style="font-size:13px;font-weight:600;color:#374151;margin:0 0 8px;">Data Inputs &amp; Signal Map</h4>
        <table style="width:100%;border-collapse:collapse;font-size:13px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
          <thead>
            <tr style="background:#f9fafb;">
              <th style="padding:8px 10px;text-align:left;font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;border-bottom:2px solid #e5e7eb;">Data Input</th>
              <th style="padding:8px 10px;text-align:center;font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;border-bottom:2px solid #e5e7eb;">Fuzziness</th>
              <th style="padding:8px 10px;text-align:left;font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;border-bottom:2px solid #e5e7eb;">Capture Method</th>
              <th style="padding:8px 10px;text-align:center;font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;border-bottom:2px solid #e5e7eb;">Customer Confirmed</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`
  }

  const pills: string[] = []
  if (cr.passRate) pills.push(`<span style="display:inline-block;padding:3px 10px;border-radius:9999px;font-size:11px;font-weight:600;background:${cr.passRateConfirmed ? '#dbeafe' : '#fef3c7'};color:${cr.passRateConfirmed ? '#1d4ed8' : '#92400e'};">Current pass rate: ${esc(cr.passRate)}%${cr.passRateConfirmed ? '' : ' (to confirm)'}</span>`)
  if (cr.desiredPassRate) pills.push(`<span style="display:inline-block;padding:3px 10px;border-radius:9999px;font-size:11px;font-weight:600;background:${cr.desiredPassRateConfirmed ? '#d1fae5' : '#fef3c7'};color:${cr.desiredPassRateConfirmed ? '#065f46' : '#92400e'};">Target pass rate: ${esc(cr.desiredPassRate)}%${cr.desiredPassRateConfirmed ? '' : ' (to confirm)'}</span>`)

  const vendorPills: string[] = []
  if ((cr.docScanVendors || []).length) vendorPills.push(`<span style="display:inline-block;padding:3px 10px;border-radius:9999px;font-size:11px;background:#f3e8ff;color:#7c3aed;font-weight:500;">Doc Scan: ${cr.docScanVendors.map(esc).join(', ')}</span>`)
  if ((cr.selfieVendors || []).length) vendorPills.push(`<span style="display:inline-block;padding:3px 10px;border-radius:9999px;font-size:11px;background:#fce7f3;color:#be185d;font-weight:500;">Selfie/Liveness: ${cr.selfieVendors.map(esc).join(', ')}</span>`)
  if ((cr.deviceDataVendors || []).length) vendorPills.push(`<span style="display:inline-block;padding:3px 10px;border-radius:9999px;font-size:11px;background:#fff7ed;color:#c2410c;font-weight:500;">Device Intelligence: ${cr.deviceDataVendors.map(esc).join(', ')}</span>`)

  const nationalIdHtml = (cr.nationalIds || []).length > 0
    ? `<div style="margin-top:12px;"><span style="font-size:12px;font-weight:600;color:#374151;">Accepted IDs:</span> <span style="font-size:12px;color:#6b7280;">${cr.nationalIds.map((id) => esc(idLabel(id, cr.country))).join(', ')}</span></div>`
    : ''

  const notesHtml = cr.notes
    ? `<div style="margin-top:12px;padding:10px 14px;background:#fffbeb;border-left:3px solid #f59e0b;border-radius:4px;font-size:12px;color:#92400e;">${esc(cr.notes)}</div>`
    : ''

  return `
    <div style="background:#ffffff;border:1px solid #e5e7eb;border-radius:12px;padding:24px;margin-bottom:20px;box-shadow:0 1px 3px rgba(0,0,0,0.06);">
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:16px;flex-wrap:wrap;">
        <h3 style="font-size:18px;font-weight:700;color:#111827;margin:0;">${esc(name)}</h3>
        <span style="font-size:11px;font-weight:600;color:#fff;background:${rulesetColor};padding:3px 12px;border-radius:9999px;">${esc(ruleset)}</span>
        ${cr.govIdRequired ? '<span style="font-size:11px;font-weight:600;color:#065f46;background:#d1fae5;padding:3px 12px;border-radius:9999px;">Gov ID Required</span>' : ''}
      </div>
      ${pills.length > 0 || vendorPills.length > 0 ? `<div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px;">${[...pills, ...vendorPills].join('')}</div>` : ''}
      ${nationalIdHtml}
      ${dataInputsHtml}
      ${notesHtml}
    </div>`
}

export function exportBlueprintPdf(scope: ScopeData, _qualification: Record<string, string> | null): void {
  const bp = scope.blueprintConfig
  const customerName = scope.customerName || 'Customer'
  const today = new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })

  // --- Context & Metrics ---
  const hasContext = bp.businessModel || bp.monthlyOnboardingVolume || bp.strategicPriorities || bp.stpRate || bp.fraudRate
  const contextSection = hasContext ? `
    <div style="margin-bottom:40px;">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:20px;">
        <div style="width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg,#06b6d4,#22d3ee);display:flex;align-items:center;justify-content:center;">
          <span style="color:#fff;font-size:18px;">&#9432;</span>
        </div>
        <h2 style="font-size:20px;font-weight:700;color:#111827;margin:0;">Context & Current Metrics</h2>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
        ${bp.businessModel ? `<div style="background:#fff;border:1px solid #e5e7eb;border-radius:8px;padding:16px;"><p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#0891b2;margin:0 0 6px;">Business Model</p><p style="font-size:13px;color:#374151;margin:0;line-height:1.6;">${esc(bp.businessModel)}</p></div>` : ''}
        ${bp.strategicPriorities ? `<div style="background:#fff;border:1px solid #e5e7eb;border-radius:8px;padding:16px;"><p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#0891b2;margin:0 0 6px;">Strategic Priorities</p><p style="font-size:13px;color:#374151;margin:0;line-height:1.6;">${esc(bp.strategicPriorities)}</p></div>` : ''}
      </div>
      ${bp.monthlyOnboardingVolume ? `<div style="margin-top:12px;background:#f0fdfa;border-left:4px solid #14b8a6;border-radius:8px;padding:12px 16px;"><p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#0d9488;margin:0 0 4px;">Monthly Onboarding Volume</p><p style="font-size:13px;color:#374151;margin:0;">${esc(bp.monthlyOnboardingVolume)}</p></div>` : ''}
      ${bp.stpRate || bp.fraudRate ? `
        <div style="display:flex;gap:16px;margin-top:16px;">
          ${bp.stpRate ? `<div style="flex:1;background:#fff;border:1px solid #e5e7eb;border-radius:8px;padding:16px;text-align:center;"><p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;margin:0 0 6px;">STP Rate</p><p style="font-size:28px;font-weight:800;color:#111827;margin:0;">${esc(bp.stpRate)}</p>${!bp.stpRateConfirmed ? '<p style="font-size:10px;color:#f59e0b;margin:4px 0 0;">To be confirmed</p>' : ''}</div>` : ''}
          ${bp.fraudRate ? `<div style="flex:1;background:#fff;border:1px solid #e5e7eb;border-radius:8px;padding:16px;text-align:center;"><p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;margin:0 0 6px;">Fraud Rate</p><p style="font-size:28px;font-weight:800;color:#ef4444;margin:0;">${esc(bp.fraudRate)}</p>${!bp.fraudRateConfirmed ? '<p style="font-size:10px;color:#f59e0b;margin:4px 0 0;">To be confirmed</p>' : ''}</div>` : ''}
        </div>` : ''}
    </div>` : ''

  // --- Verification Stack ---
  const filledLayers = (bp.verificationStack || []).filter((v) => v.vendor.trim())
  const verificationSection = filledLayers.length > 0 ? `
    <div style="margin-bottom:40px;page-break-inside:avoid;">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:20px;">
        <div style="width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg,#f43f5e,#fb7185);display:flex;align-items:center;justify-content:center;">
          <span style="color:#fff;font-size:18px;">&#128737;</span>
        </div>
        <h2 style="font-size:20px;font-weight:700;color:#111827;margin:0;">Current Verification Stack</h2>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:13px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
        <thead>
          <tr style="background:#f9fafb;">
            <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;border-bottom:2px solid #e5e7eb;">Layer</th>
            <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;border-bottom:2px solid #e5e7eb;">Vendor / Approach</th>
            <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;border-bottom:2px solid #e5e7eb;">Owner</th>
            <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;border-bottom:2px solid #e5e7eb;">Pain Points</th>
          </tr>
        </thead>
        <tbody>
          ${filledLayers.map((v) => {
            const label = v.layer === 'other' ? esc(v.customLabel || 'Other') : esc(v.layer)
            return `<tr>
              <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:500;color:#111827;">${label}</td>
              <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#374151;">${esc(v.vendor)}</td>
              <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">${v.owner ? esc(v.owner) : '—'}</td>
              <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;font-size:12px;">${v.painPoints ? esc(v.painPoints) : '—'}</td>
            </tr>`
          }).join('')}
        </tbody>
      </table>
    </div>` : ''

  // --- Problem Hypothesis ---
  const hypothesisSection = (bp.problemHypothesis || bp.surfacePain || bp.deepPain || bp.regulatoryContext) ? `
    <div style="margin-bottom:40px;">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:20px;">
        <div style="width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg,#8b5cf6,#a78bfa);display:flex;align-items:center;justify-content:center;">
          <span style="color:#fff;font-size:18px;">&#9733;</span>
        </div>
        <h2 style="font-size:20px;font-weight:700;color:#111827;margin:0;">Understanding Your Challenges</h2>
      </div>
      ${bp.problemHypothesis ? `
        <div style="background:linear-gradient(135deg,#f5f3ff,#ede9fe);border-left:4px solid #8b5cf6;border-radius:8px;padding:20px;margin-bottom:16px;">
          <p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#7c3aed;margin:0 0 6px;">Our Understanding</p>
          <p style="font-size:14px;color:#374151;margin:0;line-height:1.7;">${esc(bp.problemHypothesis)}</p>
        </div>` : ''}
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
        ${bp.surfacePain ? `
          <div style="background:#fff;border:1px solid #e5e7eb;border-radius:8px;padding:16px;">
            <p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#dc2626;margin:0 0 6px;">Immediate Challenge</p>
            <p style="font-size:13px;color:#374151;margin:0;line-height:1.6;">${esc(bp.surfacePain)}</p>
          </div>` : ''}
        ${bp.deepPain ? `
          <div style="background:#fff;border:1px solid #e5e7eb;border-radius:8px;padding:16px;">
            <p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#9333ea;margin:0 0 6px;">Underlying Impact</p>
            <p style="font-size:13px;color:#374151;margin:0;line-height:1.6;">${esc(bp.deepPain)}</p>
          </div>` : ''}
      </div>
      ${bp.whyNow ? `
        <div style="margin-top:16px;background:#fff;border:1px solid #e5e7eb;border-left:4px solid #f59e0b;border-radius:8px;padding:16px;">
          <p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#d97706;margin:0 0 6px;">Why Now?</p>
          <p style="font-size:13px;color:#374151;margin:0;line-height:1.6;">${esc(bp.whyNow)}</p>
        </div>` : ''}
      ${bp.regulatoryContext ? `
        <div style="margin-top:16px;background:linear-gradient(135deg,#fff1f2,#ffe4e6);border-left:4px solid #f43f5e;border-radius:8px;padding:20px;">
          <p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#e11d48;margin:0 0 6px;">Regulatory &amp; Industry Context</p>
          <p style="font-size:13px;color:#374151;margin:0;line-height:1.6;">${esc(bp.regulatoryContext)}</p>
        </div>` : ''}
    </div>` : ''

  // --- As-Is Architecture ---
  const hasVendors = bp.currentVendors.length > 0
  const vendorRows = bp.currentVendors.map((v) => {
    const funcColors: Record<string, string> = { kyc: '#3b82f6', aml: '#ef4444', idv: '#8b5cf6', fraud: '#f59e0b', biometrics: '#ec4899', monitoring: '#06b6d4', other: '#6b7280' }
    const color = funcColors[v.function] || '#6b7280'
    return `<tr>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;"><span style="display:inline-block;padding:2px 10px;border-radius:9999px;font-size:11px;font-weight:600;background:${color}15;color:${color};border:1px solid ${color}30;">${esc(v.function.toUpperCase())}</span></td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:500;color:#111827;">${esc(v.vendorName)}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#6b7280;">${v.notes ? esc(v.notes) : '—'}</td>
    </tr>`
  }).join('')

  const discoveryQuestions: { label: string; field: keyof typeof bp; icon: string }[] = [
    { label: 'Jurisdictions in Scope', field: 'jurisdictionsInScope', icon: '&#127760;' },
    { label: 'Current Onboarding Flow', field: 'onboardingJourney', icon: '&#128100;' },
    { label: 'Pre-Transaction Checks', field: 'checksBeforeTransact', icon: '&#9989;' },
    { label: 'Risk Segmentation', field: 'riskSegmentation', icon: '&#128202;' },
    { label: 'EDD &amp; Manual Review Triggers', field: 'eddTriggers', icon: '&#128270;' },
    { label: 'Sanctions, PEP &amp; Adverse Media', field: 'sanctionsPepScreening', icon: '&#128737;' },
    { label: 'Ongoing Monitoring', field: 'ongoingMonitoring', icon: '&#128260;' },
    { label: 'Friction &amp; Pain Points', field: 'frictionFalsePositives', icon: '&#9888;' },
  ]
  const answeredDiscovery = discoveryQuestions.filter((q) => (bp[q.field] as string)?.trim())
  const discoveryHtml = answeredDiscovery.length > 0
    ? answeredDiscovery.map((q) => `
        <div style="background:#fff;border:1px solid #e5e7eb;border-radius:8px;padding:16px;break-inside:avoid;">
          <p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#2563eb;margin:0 0 6px;">${q.icon} ${q.label}</p>
          <p style="font-size:13px;color:#374151;margin:0;line-height:1.6;">${esc(bp[q.field] as string)}</p>
        </div>`).join('')
    : ''

  const architectureSection = (bp.currentProcess || bp.integrationMethod || hasVendors || answeredDiscovery.length > 0) ? `
    <div style="margin-bottom:40px;page-break-inside:avoid;">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:20px;">
        <div style="width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg,#3b82f6,#60a5fa);display:flex;align-items:center;justify-content:center;">
          <span style="color:#fff;font-size:18px;">&#9881;</span>
        </div>
        <h2 style="font-size:20px;font-weight:700;color:#111827;margin:0;">Current Architecture &amp; Process</h2>
      </div>
      ${bp.currentProcess ? `
        <div style="background:#eff6ff;border-left:4px solid #2563eb;border-radius:8px;padding:16px;margin-bottom:16px;">
          <p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#2563eb;margin:0 0 4px;">Current Verification Process</p>
          <p style="font-size:13px;color:#374151;margin:0;line-height:1.6;white-space:pre-wrap;">${esc(bp.currentProcess)}</p>
        </div>` : ''}
      ${bp.integrationMethod ? `
        <div style="background:#eff6ff;border-left:4px solid #3b82f6;border-radius:8px;padding:16px;margin-bottom:16px;">
          <p style="font-size:11px;font-weight:600;text-transform:uppercase;color:#2563eb;margin:0 0 4px;">Integration Method</p>
          <p style="font-size:13px;color:#374151;margin:0;">${esc(bp.integrationMethod)}</p>
        </div>` : ''}
      ${hasVendors ? `
        <div style="margin-bottom:20px;">
          <h3 style="font-size:15px;font-weight:600;color:#374151;margin:0 0 10px;">Current Vendor Stack</h3>
          <table style="width:100%;border-collapse:collapse;font-size:13px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
            <thead>
              <tr style="background:#f9fafb;">
                <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;border-bottom:2px solid #e5e7eb;">Function</th>
                <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;border-bottom:2px solid #e5e7eb;">Vendor</th>
                <th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;text-transform:uppercase;color:#6b7280;border-bottom:2px solid #e5e7eb;">Notes</th>
              </tr>
            </thead>
            <tbody>${vendorRows}</tbody>
          </table>
        </div>` : ''}
      ${discoveryHtml ? `
        <h3 style="font-size:15px;font-weight:600;color:#374151;margin:0 0 12px;">Discovery Findings</h3>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">${discoveryHtml}</div>` : ''}
    </div>` : ''

  // --- Country Rulesets ---
  const countrySection = bp.countryRulesets.length > 0 ? `
    <div style="margin-bottom:40px;">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:20px;">
        <div style="width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg,#10b981,#34d399);display:flex;align-items:center;justify-content:center;">
          <span style="color:#fff;font-size:18px;">&#127758;</span>
        </div>
        <h2 style="font-size:20px;font-weight:700;color:#111827;margin:0;">Country Verification Profiles</h2>
      </div>
      ${bp.countryRulesets.map(buildCountryCard).join('')}
    </div>` : ''

  // --- Assemble full HTML ---
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Pre-Sales Blueprint — ${esc(customerName)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; color: #374151; line-height: 1.6; background: #f8fafc; }
    @media print {
      body { background: #fff; }
      .page-wrapper { box-shadow: none !important; }
      .no-print { display: none !important; }
    }
    @page { margin: 15mm; size: A4; }
    table { border-collapse: collapse; }
  </style>
</head>
<body>
  <div class="page-wrapper" style="max-width:900px;margin:0 auto;background:#fff;min-height:100vh;">

    <!-- Header -->
    <div style="background:linear-gradient(135deg,#1e1b4b,#312e81,#4338ca);padding:48px 40px 40px;color:#fff;">
      <div style="display:flex;align-items:center;gap:14px;margin-bottom:24px;">
        <div style="width:44px;height:44px;border-radius:12px;background:rgba(255,255,255,0.15);display:flex;align-items:center;justify-content:center;backdrop-filter:blur(8px);">
          <span style="font-size:22px;">&#9670;</span>
        </div>
        <div>
          <p style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:1.5px;color:rgba(255,255,255,0.6);margin:0;">Pre-Sales Blueprint</p>
          <p style="font-size:11px;color:rgba(255,255,255,0.4);margin:2px 0 0;">Prepared by FrankieOne</p>
        </div>
      </div>
      <h1 style="font-size:32px;font-weight:800;margin:0 0 8px;letter-spacing:-0.5px;">${esc(customerName)}</h1>
      <p style="font-size:14px;color:rgba(255,255,255,0.6);margin:0;">${today}</p>
      ${bp.countryRulesets.length > 0 ? `
        <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:20px;">
          ${bp.countryRulesets.filter((cr) => cr.country).map((cr) => `<span style="display:inline-block;padding:4px 14px;border-radius:9999px;font-size:12px;font-weight:500;background:rgba(255,255,255,0.12);color:rgba(255,255,255,0.85);border:1px solid rgba(255,255,255,0.15);">${esc(countryName(cr.country))}</span>`).join('')}
        </div>` : ''}
    </div>

    <!-- Body -->
    <div style="padding:40px;">
      ${contextSection}
      ${hypothesisSection}
      ${verificationSection}
      ${architectureSection}
      ${countrySection}

      <!-- Footer -->
      <div style="border-top:1px solid #e5e7eb;padding-top:24px;margin-top:40px;text-align:center;">
        <p style="font-size:12px;color:#9ca3af;margin:0;">This document was prepared by FrankieOne as part of our pre-sales discovery process.</p>
        <p style="font-size:12px;color:#9ca3af;margin:4px 0 0;">The information reflects our current understanding and is subject to further validation.</p>
      </div>
    </div>

  </div>
  <script>window.print();</script>
</body>
</html>`

  const win = window.open('', '_blank')
  if (!win) return
  win.document.write(html)
  win.document.close()
}
